import React, { useEffect, useRef, useState } from 'react';

import {
  PlacesAutocomplete,
  type PlacePick,
} from '@/design-system/places-autocomplete';

export type { PlacePick };

interface AddressAutocompleteProps {
  inputId?: string;
  value: string;
  onChange: (value: string) => void;
  onPick: (place: PlacePick) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Screen-reader label, for forms that repeat this field per stop. */
  ariaLabel?: string;
}

const inputCls =
  'w-full rounded-gsm border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500';

/**
 * Thin wrapper over the kit `<PlacesAutocomplete>` that defers mounting the
 * live Google-Places field until the user focuses it.
 *
 * The kit component fetches predictions + opens its dropdown whenever `value`
 * is non-empty once the SDK is ready — including on a programmatic prefill
 * (edit mode). That makes the suggestion list pop open the moment the modal
 * opens on a pre-filled address. We can't fix that in the kit (it's synced
 * from the design-system vault, never hand-edited), so we gate it here: show a
 * plain input carrying the current value, and only swap in the real
 * autocomplete once the user actually engages the field.
 */
const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  inputId,
  value,
  onChange,
  onPick,
  placeholder,
  disabled,
  ariaLabel,
}) => {
  const [active, setActive] = useState(false);
  const liveRef = useRef<HTMLInputElement>(null);

  // When we swap to the live field, move focus onto it so the user keeps
  // typing without a second click.
  useEffect(() => {
    if (active) liveRef.current?.focus();
  }, [active]);

  if (!active) {
    return (
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => {
          setActive(true);
          onChange(e.target.value);
        }}
        onFocus={() => setActive(true)}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        autoComplete="off"
        className={inputCls}
      />
    );
  }

  return (
    <PlacesAutocomplete
      ref={liveRef}
      inputId={inputId}
      value={value}
      onChange={onChange}
      onPick={onPick}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
};

export default AddressAutocomplete;
